import axios from '../axios';
import { Normalize } from '../phone';
import { GetPageRequestID } from './pagerequests';
import * as models from './models';
import { Session } from './session';

export function ShippingOptions(params?: Record<string, string>) {
    return axios.get('/shipping-options', { params }).
        then(resp => models.ShippingOptionsHyejooResponse.fromJSON(resp.data));
}

export async function NewShippingOption(userInit: models.IUser, shippingOptionInit: models.IShippingOption): Promise<models.ShippingOption> {
    const shippingOption = new models.ShippingOption(shippingOptionInit);
    const user = new models.User(userInit);
    user.phone = Normalize(user.phone);
    user.tz ??= Intl.DateTimeFormat().resolvedOptions().timeZone;
    user.pageRequestID ??= await GetPageRequestID();

    const shippingAddress = shippingOption.address;
    if (!shippingAddress) {
        throw new Error('shippingOption.address is required');
    }

    // Backfill from shippingOption + the active session — callers can lose
    // user.email/phone when a saved shippingOption lacks them, but the
    // customer-api requires both.
    if (!user.email) {
        user.email = shippingAddress.emailAddress?.email ?? '';
    }
    if (!user.phone) {
        user.phone = Normalize(shippingAddress.phoneNumber?.phone ?? '');
    }
    if (!user.email || !user.phone) {
        const sess = await Session().catch(() => undefined);
        if (!user.email && sess?.user.emailAddress.email) {
            user.email = sess.user.emailAddress.email;
        }
        if (!user.phone && sess?.user.phoneNumber.phone) {
            user.phone = Normalize(sess.user.phoneNumber.phone);
        }
    }

    const countryID = shippingAddress.country?.id || shippingAddress.countryID;
    const stateID = shippingAddress.state?.id || shippingAddress.stateID;

    const resp = await axios.post('/shipping-options', {
        'user': user,
        'fullName': shippingAddress.fullName,
        'company': shippingAddress.organization,
        'addressLine1': shippingAddress.addressLine1,
        'addressLine2': shippingAddress.addressLine2,
        'city': shippingAddress.city,
        'state': stateID ? {
            'id': stateID,
        } : undefined,
        'zip': shippingAddress.postalCode,
        'country': {
            'id': countryID,
        },
        'phone': Normalize(shippingAddress.phoneNumber?.phone ?? ''),
        'email': shippingAddress.emailAddress?.email ?? '',
        'label': shippingOption.label,
    });

    // The response renders email/phone through the address's emailaddresses/
    // phonenumbers lookups, and a deduped address row can reference an email
    // row that no longer exists — the created option then comes back with no
    // email at all. Brand checkouts replace their local shippingOption with
    // this return value, so losing the field here cascades into the next
    // POST /payment-options going out with user.email empty and the API
    // rejecting the order with `user.email: required`. Keep what we sent.
    const created = models.ShippingOption.fromJSON(resp.data);
    if (!created.address.emailAddress?.email && shippingAddress.emailAddress?.email) {
        created.address.emailAddress = new models.EmailAddress({ email: shippingAddress.emailAddress.email });
    }
    if (!created.address.phoneNumber?.phone && shippingAddress.phoneNumber?.phone) {
        created.address.phoneNumber = new models.PhoneNumber({ phone: Normalize(shippingAddress.phoneNumber.phone) });
    }
    return created;
}

export function DeleteShippingOption(shippingOptionID: string) {
    return axios.delete(`/shipping-options/${shippingOptionID}`);
}

export function RestoreShippingOption(shippingOptionID: string) {
    return axios.patch(`/shipping-options/${shippingOptionID}`, { 'active': true });
}

export function UpdateShippingOptionLabel(shippingOptionID: string, label: string) {
    return axios.patch(`/shipping-options/${shippingOptionID}`, { 'label': label });
}
